import { Component, OnDestroy, effect, input, output } from '@angular/core';
import { ReactiveFormsModule, FormControl, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import {
  FilterMode,
  FILTER_MODE
} from '@view/shared/constants/filterMode.const';
import { AppTranslationPipe } from '@adapter/i18n/app-translation.pipe';

@Component({
  selector: 'app-report-filter-bar',
  imports: [ReactiveFormsModule, AppTranslationPipe],
  templateUrl: './report-filter-bar.component.html',
  styleUrl: './report-filter-bar.component.scss'
})
export class ReportFilterBarComponent implements OnDestroy {
  protected readonly FILTER_MODE = FILTER_MODE;

  filterMode = input<FilterMode>(FILTER_MODE.LOAD_ID);
  filterText = input<string>('');
  isLoading = input<boolean>(false);
  isRefreshing = input<boolean>(false);

  filterModeChange = output<FilterMode>();
  filterTextChange = output<string>();
  search = output<void>();
  refresh = output<void>();
  reset = output<void>();

  protected filterControl = new FormControl('', { nonNullable: true });
  private subscription: Subscription;

  constructor() {
    effect(() => {
      const text = this.filterText();
      if (this.filterControl.value !== text) {
        this.filterControl.setValue(text, { emitEvent: false });
        this.filterControl.markAsPristine();
      }
    });

    effect(() => {
      this.updateValidators(this.filterMode());
    });

    this.subscription = this.filterControl.valueChanges.subscribe((value) => {
      this.filterTextChange.emit(value);
    });
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  private updateValidators(mode: FilterMode): void {
    if (mode === FILTER_MODE.POLICY) {
      this.filterControl.setValidators([
        Validators.minLength(12),
        Validators.maxLength(12),
        Validators.pattern(/^\d+$/)
      ]);
    } else {
      this.filterControl.setValidators([
        Validators.pattern(/^LOAD-CME-\d{13}-[0-9A-F]{8}$/i)
      ]);
    }
    this.filterControl.updateValueAndValidity();
  }

  protected get showFilterError(): boolean {
    return (
      !!this.filterControl.dirty &&
      !!this.filterControl.value &&
      this.filterControl.invalid
    );
  }

  protected get hasPolicyPatternError(): boolean {
    return (
      this.filterMode() === FILTER_MODE.POLICY &&
      !!this.filterControl.errors?.['pattern']
    );
  }

  protected get hasPolicyLengthError(): boolean {
    return (
      this.filterMode() === FILTER_MODE.POLICY &&
      !!this.filterControl.errors?.['minlength']
    );
  }

  protected get hasLoadIdPatternError(): boolean {
    return (
      this.filterMode() === FILTER_MODE.LOAD_ID &&
      !!this.filterControl.errors?.['pattern']
    );
  }

  protected onSearch(): void {
    if (this.filterControl.valid || !this.filterControl.value) {
      this.search.emit();
    }
  }
}
